import type {
  ScheduledWorkflowDeliverablesListParams,
  ScheduledWorkflowRunsListParams,
  ScheduledWorkflowsListParams,
} from "./types"

export type ScheduledWorkflowFileUrlKind = "content" | "download" | "render-url" | "panel" | "share"

export type ScheduledWorkflowLifecycleAction = "pause" | "activate" | "archive"

type QueryValue = string | number | boolean | undefined

export const SCHEDULED_WORKFLOWS_DEFAULT_LIMIT = 20

export const SCHEDULED_WORKFLOWS_MAX_LIMIT = 100

export function toQueryString(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.append(key, String(value))
  })
  const query = search.toString()
  return query === "" ? "" : `?${query}`
}

export function scheduledWorkflowsBaseUrl(accountAlias: string): string {
  return `/api/${encodeURIComponent(accountAlias)}/ai/scheduled-workflows`
}

export function scheduledWorkflowsUrl(accountAlias: string, params: ScheduledWorkflowsListParams = {}): string {
  const { cursor, limit, includeArchived } = params
  return `${scheduledWorkflowsBaseUrl(accountAlias)}${toQueryString({ cursor, limit, includeArchived })}`
}

export function scheduledWorkflowUrl(accountAlias: string, scheduledWorkflowId: string): string {
  return `${scheduledWorkflowsBaseUrl(accountAlias)}/${encodeURIComponent(scheduledWorkflowId)}`
}

export function scheduledWorkflowActionUrl(accountAlias: string, scheduledWorkflowId: string, action: ScheduledWorkflowLifecycleAction): string {
  return `${scheduledWorkflowUrl(accountAlias, scheduledWorkflowId)}/${action}`
}

export function scheduledWorkflowRunsUrl(accountAlias: string, scheduledWorkflowId: string, params: ScheduledWorkflowRunsListParams = {}): string {
  const { cursor, limit, failuresOnly } = params
  return `${scheduledWorkflowUrl(accountAlias, scheduledWorkflowId)}/runs${toQueryString({ cursor, limit, failuresOnly })}`
}

export function scheduledWorkflowDeliverablesUrl(accountAlias: string, params: ScheduledWorkflowDeliverablesListParams = {}): string {
  const { cursor, limit, workflowId, excludeArchived } = params
  return `${scheduledWorkflowsBaseUrl(accountAlias)}/deliverables${toQueryString({ cursor, limit, workflowId, excludeArchived })}`
}

export function scheduledWorkflowDeliverableUrl(accountAlias: string, deliverableId: string): string {
  return `${scheduledWorkflowsBaseUrl(accountAlias)}/deliverables/${encodeURIComponent(deliverableId)}`
}

export function scheduledWorkflowDeliverableReadUrl(accountAlias: string, deliverableId: string): string {
  return `${scheduledWorkflowDeliverableUrl(accountAlias, deliverableId)}/read`
}

export function scheduledWorkflowDeliverablesUnreadCountUrl(accountAlias: string): string {
  return `${scheduledWorkflowsBaseUrl(accountAlias)}/deliverables/unread-count`
}

export interface ScheduledWorkflowFileUrlParams {
  accountAlias: string
  deliverableId: string
  fileId: string
}

export function buildScheduledWorkflowFileUrl({ accountAlias, deliverableId, fileId }: ScheduledWorkflowFileUrlParams, kind: ScheduledWorkflowFileUrlKind): string {
  return `${scheduledWorkflowDeliverableUrl(accountAlias, deliverableId)}/files/${encodeURIComponent(fileId)}/${kind}`
}
